import React, { useState } from "react";
import { ShieldCheck, Phone, EnvelopeSimple, MapPin, Clock, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices } from "../../lib/services";
import SEO from "../../components/SEO";
import { toast } from "sonner";

export default function Contact() {
  const c = useContent();
  const site = useSite();
  const services = useServices();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contacts", form);
      toast.success(pick(c, "contact_page.form_success_toast", "Thanks! We'll reach out within one business day."));
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <SEO
        path="/contact"
        title="Contact ATC Home Pro's — Free Estimates"
        description={`Reach ATC Home Pro's in ${site?.address || "Loganville, PA"}. Call ${site?.phone || ""}, email ${site?.email || ""}, or send a message for a free estimate. Lifetime labor warranty.`}
      />
      <section className="bg-atc-black text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-30" />
        <div className="container-max relative">
          <div className="overline mb-4 text-atc-red">{pick(c, "contact_page.hero_overline", "About & Contact")}</div>
          <h1 className="h-display">{pick(c, "contact_page.hero_title_line_1", "Let's build")}<br/>{pick(c, "contact_page.hero_title_line_2", "something good.")}</h1>
        </div>
      </section>

      <section className="section-pad bg-atc-paper">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">{pick(c, "contact_page.story_overline", "Our story")}</div>
            <h2 className="h-section">{pick(c, "contact_page.story_title_line_1", "Family-owned.")}<br/>{pick(c, "contact_page.story_title_line_2", "Locally loyal.")}</h2>
            <div className="rule-red mt-6" />
          </div>
          <div className="md:col-span-8">
            <p className="text-lg leading-relaxed text-atc-charcoal">{pick(c, "contact_page.story_body_1", "")}</p>
            <p className="mt-5 text-lg leading-relaxed text-atc-charcoal">{pick(c, "contact_page.story_body_2", "")}</p>
          </div>
        </div>
      </section>

      <section className="bg-atc-red text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:3rem_3rem] opacity-20" />
        <div className="container-max relative flex items-center gap-6">
          <ShieldCheck size={56} weight="bold" />
          <div>
            <div className="font-display text-3xl md:text-4xl uppercase leading-none">{pick(c, "contact_page.warranty_title", "Lifetime labor warranty")}</div>
            <p className="mt-2 text-white/90">{pick(c, "contact_page.warranty_body", "Quality that lasts — guaranteed on every ATC Home Pro's project.")}</p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white border-t-2 border-atc-black">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="overline mb-3">{pick(c, "contact_page.form_overline", "Get in touch")}</div>
            <h2 className="h-section mb-8">{pick(c, "contact_page.form_title_line_1", "Send us a")}<br/>{pick(c, "contact_page.form_title_line_2", "message.")}</h2>
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block font-heading uppercase tracking-widest text-xs text-atc-black mb-2">Full Name *</label>
                  <input required value={form.name} onChange={update("name")} className="w-full bg-transparent border-0 border-b-2 border-atc-black px-0 py-2 focus:outline-none focus:border-atc-red font-body" data-testid="input-name" />
                </div>
                <div>
                  <label className="block font-heading uppercase tracking-widest text-xs text-atc-black mb-2">Email *</label>
                  <input required type="email" value={form.email} onChange={update("email")} className="w-full bg-transparent border-0 border-b-2 border-atc-black px-0 py-2 focus:outline-none focus:border-atc-red font-body" data-testid="input-email" />
                </div>
                <div>
                  <label className="block font-heading uppercase tracking-widest text-xs text-atc-black mb-2">Phone</label>
                  <input type="tel" value={form.phone} onChange={update("phone")} className="w-full bg-transparent border-0 border-b-2 border-atc-black px-0 py-2 focus:outline-none focus:border-atc-red font-body" data-testid="input-phone" />
                </div>
                <div>
                  <label className="block font-heading uppercase tracking-widest text-xs text-atc-black mb-2">Service Needed</label>
                  <select value={form.service} onChange={update("service")} className="w-full bg-transparent border-0 border-b-2 border-atc-black px-0 py-2 focus:outline-none focus:border-atc-red font-body" data-testid="input-service">
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-heading uppercase tracking-widest text-xs text-atc-black mb-2">Tell us about your project *</label>
                <textarea required rows={5} value={form.message} onChange={update("message")} className="w-full bg-transparent border-2 border-atc-black px-3 py-2 focus:outline-none focus:border-atc-red font-body" data-testid="input-message" />
              </div>
              <button disabled={sending} type="submit" className="btn-primary disabled:opacity-60" data-testid="contact-submit">
                <PaperPlaneTilt size={18} weight="bold" /> {sending ? "Sending…" : pick(c, "contact_page.form_submit_label", "Send Message")}
              </button>
            </form>
          </div>

          <aside className="md:col-span-5 space-y-6">
            <div className="bg-atc-black text-white p-8">
              <div className="overline text-white mb-5">{pick(c, "contact_page.info_overline", "Direct line")}</div>
              <ul className="space-y-5 text-sm">
                {site?.phone && (
                  <li>
                    <div className="flex items-center gap-3"><Phone size={18} weight="bold" className="text-atc-red" /><span className="font-heading uppercase tracking-widest text-xs text-white/60">Phone</span></div>
                    <a href={`tel:${site.phone}`} className="block mt-1 text-2xl font-display" data-testid="contact-phone">{site.phone}</a>
                  </li>
                )}
                {site?.email && (
                  <li>
                    <div className="flex items-center gap-3"><EnvelopeSimple size={18} weight="bold" className="text-atc-red" /><span className="font-heading uppercase tracking-widest text-xs text-white/60">Email</span></div>
                    <a href={`mailto:${site.email}`} className="block mt-1 text-lg font-body break-all" data-testid="contact-email">{site.email}</a>
                  </li>
                )}
                {site?.address && (
                  <li>
                    <div className="flex items-center gap-3"><MapPin size={18} weight="bold" className="text-atc-red" /><span className="font-heading uppercase tracking-widest text-xs text-white/60">Location</span></div>
                    <div className="mt-1 font-body">{site.address}</div>
                  </li>
                )}
                <li>
                  <div className="flex items-center gap-3"><Clock size={18} weight="bold" className="text-atc-red" /><span className="font-heading uppercase tracking-widest text-xs text-white/60">Hours</span></div>
                  <div className="mt-1 font-mono text-sm space-y-1">
                    <div>Mon–Fri · {site?.hours_mon_fri}</div>
                    <div>Sat · {site?.hours_sat}</div>
                    <div>Sun · {site?.hours_sun}</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="border-2 border-atc-black p-6">
              <div className="overline mb-3">{pick(c, "contact_page.areas_overline", "Service area")}</div>
              <div className="flex flex-wrap gap-2">
                {(site?.service_areas || []).slice(0, 10).map((a) => (
                  <span key={a} className="px-3 py-1 border border-atc-black text-xs font-mono uppercase">{a}</span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
